import React, { ChangeEvent, KeyboardEvent, useContext, useEffect, useState } from 'react';
import { Card } from './Card';
import { TotalViews } from '../mediaPlayback/TotalViews';
import { Partials } from '../mediaPlayback/Partials';
import { Completed } from '../mediaPlayback/Completed';
import { Campaign } from '../mediaPlayback/Campaign';
import { List } from '../mediaPlayback/List';
import { MediaPlayback, MediaPlaybackTotals, getMediaPlaybackPage, getMediaPlaybackTotals, getPreviousMediaPlaybackTotals } from '../../model/MediaPlayback';
import { Filter } from '../../model/Filter';
import { AccountContext } from '../../contexts/AccountContext';

const DISPLAY_PAGE_SIZE = 3;
const REQUEST_PAGE_SIZE = 21;

const emptyTotals: MediaPlaybackTotals = { fullViews: 0, partialViews: 0, mostViewedCampaign: { id: '', name: '', count: 0 } };

const MediaPlaybackCard = ({ filter }: Props) => {
  const params = useContext(AccountContext);
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<MediaPlayback[]>([]);
  const [hasMoreOnServer, setHasMoreOnServer] = useState(true);
  const [nextServerPage, setNextServerPage] = useState(0);
  const [loadingList, setLoadingList] = useState(false);
  const [totals, setTotals] = useState<MediaPlaybackTotals>(emptyTotals);
  const [previousTotals, setPreviousTotals] = useState<MediaPlaybackTotals>(emptyTotals);

  const ensureLoaded = async (targetPage: number, search: string, loadedItems: MediaPlayback[], more: boolean, serverPage: number) => {
    while(loadedItems.length < targetPage * DISPLAY_PAGE_SIZE && more) {
      const data = await getMediaPlaybackPage(filter, serverPage, REQUEST_PAGE_SIZE, search, params);
      loadedItems = [...loadedItems, ...data.list];
      more = Boolean(data.more);
      serverPage += 1;
    }
    return { items: loadedItems, more, serverPage };
  }

  const load = async (targetPage: number, search: string) => {
    setLoadingList(true);
    try {
      const result = await ensureLoaded(targetPage, search, [], true, 0);
      setItems(result.items);
      setHasMoreOnServer(result.more);
      setNextServerPage(result.serverPage);
      setPage(targetPage);
    }
    finally {
      setLoadingList(false);
    }
  }

  const loadTotals = async () => {
    const t = await getMediaPlaybackTotals(filter, params);
    setTotals(t);
    const previous = await getPreviousMediaPlaybackTotals(filter, params);
    setPreviousTotals(previous);
  }

  useEffect(() => {
    loadTotals();
    load(1, appliedSearch);
  }, [filter]);

  const onChangeSearchInput = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  }

  const onSearch = () => {
    setAppliedSearch(searchInput);
    load(1, searchInput);
  }

  const onSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if(event.key === 'Enter') {
      onSearch();
    }
  }

  const onPageChange = async (newPage: number) => {
    setLoadingList(true);
    try {
      const result = await ensureLoaded(newPage, appliedSearch, items, hasMoreOnServer, nextServerPage);
      setItems(result.items);
      setHasMoreOnServer(result.more);
      setNextServerPage(result.serverPage);
      setPage(newPage);
    }
    finally {
      setLoadingList(false);
    }
  }

  const pageItems = items.slice((page - 1) * DISPLAY_PAGE_SIZE, page * DISPLAY_PAGE_SIZE);
  const totalItems = items.length + (hasMoreOnServer ? DISPLAY_PAGE_SIZE : 0);
  const totalViews = totals.fullViews + totals.partialViews;
  const previousTotalViews = previousTotals.fullViews + previousTotals.partialViews;
  return (
    <Card className="media-playback">
      <div className="card-body">
        <h5 className="card-title pb-3 mb-3 border-bottom d-flex align-items-center justify-content-between">
          <div className="flex-grow-1">Media Playback</div>
          <div className="d-flex">
            <div className="input-group input-group-merge">
              <span className="input-group-text">
                <i className="ti ti-search" />
              </span>
              <input type="text" className="form-control form-control-sm" placeholder="Search Media" value={searchInput} onChange={onChangeSearchInput} onKeyDown={onSearchKeyDown} />
            </div>
            <button type="button" className="btn btn-primary btn-sm ms-1" disabled={loadingList} onClick={onSearch}>Search</button>
          </div>
        </h5>
        <div className="d-flex align-items-center justify-content-start flex-wrap" style={{ height: 115 }}>
          <div className="d-flex align-items-center me-5">
            <TotalViews value={totalViews} previousValue={previousTotalViews} comparePreset={filter.comparePreset} />
          </div>
          <div className="d-flex align-items-center mt-3 me-5">
            <Partials value={totals.partialViews} total={totalViews} />
          </div>
          <div className="d-flex align-items-center mt-3 me-5">
            <Completed value={totals.fullViews} total={totalViews} />
          </div>
          <div className="d-flex align-items-center me-5">
            <Campaign value={totals.mostViewedCampaign.count} total={totalViews} name={totals.mostViewedCampaign.name} />
          </div>
        </div>
        <div className="mt-4">
          <List items={pageItems} page={page} itemsPerPage={DISPLAY_PAGE_SIZE} totalItems={totalItems} onPageChange={onPageChange} />
        </div>
      </div>
    </Card>);
}

interface Props {
  filter: Filter
}

export { MediaPlaybackCard };
